import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import publicData from '../../../data/publicData.json';

const WhyChooseUsSection = ({ theme = 'dark' }) => { 
  const isDark = theme === 'dark';
  const { whyChooseUs } = publicData;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <section 
      id="why-choose-us" 
      className={`py-20 ${
        isDark 
          ? 'bg-gradient-to-r from-gray-900 to-black'
          : 'bg-gradient-to-r from-gray-100 to-white'
      }`}
    >
      <motion.div 
        className="container mx-auto px-4"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
      >
        <motion.div 
          className="text-center max-w-4xl mx-auto mb-16"
          variants={itemVariants}
        >
          <h2 className={`text-4xl font-bold mb-6 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            {whyChooseUs.title}
          </h2>
          <p className={`text-lg ${
            isDark ? 'text-gray-300' : 'text-gray-600'
          }`}>
            {whyChooseUs.description}
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={containerVariants}
        >
          {whyChooseUs.reasons.map((reason, index) => (
            <motion.div
              key={index}
              className={`p-6 rounded-xl transition-all duration-300 h-full ${
                isDark
                  ? 'hover:bg-blue-900/10 bg-gray-900/50 backdrop-blur-sm'
                  : 'hover:bg-blue-50/50 bg-white/50 backdrop-blur-sm shadow-lg'
              }`}
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className={`p-3 rounded-lg inline-block mb-4 ${
                isDark ? 'bg-blue-900/30' : 'bg-blue-100'
              }`}>
                <svg 
                  className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    d={reason.icon} 
                  />
                </svg>
              </div>
              <h3 className={`text-xl font-semibold mb-3 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                {reason.title}
              </h3>
              <p className={`mb-4 text-sm ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {reason.description}
              </p>
              <ul className={`text-sm space-y-2 ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {reason.details.map((detail, idx) => (
                  <li key={idx} className="flex items-center">
                    <span className={`mr-2 ${
                      isDark ? 'text-blue-400' : 'text-blue-600'
                    }`}>•</span>
                    {detail}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          className="text-center mt-16"
          variants={itemVariants}
        >
          <h3 className={`text-2xl font-bold mb-6 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            {whyChooseUs.cta.title}
          </h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-lg mx-auto">
            <Link
              to={whyChooseUs.cta.primaryButton.link}
              className={`px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 ${
                isDark 
                  ? 'bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
              } transform hover:scale-105 active:scale-95`}
            >
              {whyChooseUs.cta.primaryButton.text}
            </Link>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default WhyChooseUsSection; 